import { useEffect, useMemo, useRef, useState } from 'react'
import { Table } from 'components/Table'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { columns } from './constants'
import { InvoicesTableActions } from './InvoicesTableActions/InvoicesTableActions'
import {
  InvoicesTableParams,
  InvoicesTableRecord,
  InvoicesTableRequestData
} from '../types'
import { useFinancialsPopup } from 'features/Financials/Providers/FinancialsPopupProvider'
import axios, { CancelTokenSource } from 'axios'
import { useDispatch } from 'react-redux'
import { setLoading } from 'redux/store/common/slice'
import { getCognusARInvoicesData } from '../api'
import { useFinancialsCognusARContext } from 'features/Financials/Providers/FinancialsCognusARProvider'
import { FINANCIAL_STATUS } from 'features/Financials/types'
import moment from 'moment'

export const InvoicesTable = () => {
  const { invoicesDetailsPopup } = useFinancialsPopup()
  const { invoiceData } = invoicesDetailsPopup.state.payload
  const {
    state: { canInitiateDispute }
  } = useFinancialsCognusARContext()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [sortParams, setSortParams] = useState<InvoicesTableRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [tableData, setTableData] = useState<InvoicesTableRecord[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  const dispatch = useDispatch()

  const tableDataTokenRef = useRef<CancelTokenSource>()

  const disputeCanBeInitiated = useMemo(() => {
    const disputeDeadline = moment(invoiceData.period_ending)
      .add(1, 'month')
      .date(20)

    return (
      canInitiateDispute &&
      !!invoiceData.period_ending &&
      (invoiceData.status === FINANCIAL_STATUS.WAITING_FOR_RESPONSE ||
        invoiceData.status === FINANCIAL_STATUS.DISPUTED) &&
      moment().isSameOrBefore(disputeDeadline, 'day')
    )
  }, [canInitiateDispute, invoiceData.period_ending, invoiceData.status])

  useEffect(() => {
    if (invoiceData?.uuid) {
      getTableData({ ...sortParams })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData?.uuid])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = ({
    sortField,
    sortOrder,
    page
  }: InvoicesTableRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: InvoicesTableParams = {}
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getCognusARInvoicesData(invoiceData?.uuid, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(resp.data.results)
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const handleSelectRow = (selectedRowKeys) =>
    setSelectedRowKeys(selectedRowKeys)

  const updateData = () => getTableData({ ...sortParams })

  return (
    <div className="invoices-table-wrapper column gap-4">
      {!!selectedRowKeys.length && (
        <InvoicesTableActions
          totalNumber={totalNumber}
          selectedRowKeys={selectedRowKeys}
          updateData={updateData}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      )}
      <Table<InvoicesTableRecord>
        dataSource={tableData}
        columns={columns}
        rowKey={(record) => record.document_key}
        className="invoices-table"
        onChange={handleTableChange}
        onChangePage={handleChangePageSize}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        rowSelection={
          disputeCanBeInitiated
            ? {
                selectedRowKeys,
                onChange: handleSelectRow
              }
            : undefined
        }
      />
    </div>
  )
}
